import { AxiosError } from 'axios';
import { createAPIConnection } from 'services';
import { ResponseParams } from 'services/interfaces';
import { simulatedViewType } from 'stores/customer/helpers';
import { getStore } from 'stores/helpers';
import { getSimulatedPosListManage } from './helpers';
import {
	FindPharmacyRequest,
	FindPharmacyResponse,
	FindMyPharmaciesResponse,
	FindPharmaciesResponse,
	SolicitationListQueryParams,
	SolicitationListResponse,
	SimulatedPosSuggestionsResponse,
	SupervisorChangeResponse,
	SelectedFlagResponse,
	FindPharmaciesRequest,
	PosLinkPendingRequest,
	PosLinkPendingResponse,
	PosLinkValidateRequest,
	PosLinkValidateResponse,
	PosLinkGenerateRequest,
	PosLinkGenerateResponse,
	PosLinkStatusResponse,
	SearchPosResponse,
	SearchPosRequest,
	SaveBankDataRequest,
	FindBankRequest,
	FindBankResponse,
	getBankDataResponse,
	checkBankExistsResponse,
} from './interfaces';
import { requestHandler } from '../helpers';
import { FullCarouselProps } from 'front-commons/ds/components/Carousel/variants/Full/interfaces';

export * from './distributors';

export const api = createAPIConnection('', {
	onRequest(config) {
		const provider = getStore().customerReducer?.provider;

		if (provider !== 'PARCEIRO_HYPERA') Object.assign(config?.headers || {}, { 'Partner-origin': provider });
	},

	onReject(error) {
		if (error.config.url === '/pos/pre-validation/customer' && error.response.status !== 401) throw error.response;
	},
});

export const findPharmacy = async (params: FindPharmacyRequest) => {
	return requestHandler<FindPharmacyResponse>(api.get('/pos/detail', { params }));
};

export const findMyPharmaciesNoHandler = async (params?: GenericObject) => {
	const response = await api.get<FindMyPharmaciesResponse>('/pos/list-new/customer', { params });

	return response?.data;
};

export const searchPosData = async ({ forcePosList, ...params }: FindPharmaciesRequest = { summarized: true }) => {
	if (simulatedViewType() && !forcePosList) {
		const simulatedPos = await getSimulatedPosListManage();

		return simulatedPos;
	}

	try {
		const data = await requestHandler<FindPharmaciesResponse[]>(api.post('/pos/list', params), { throwData: true });
		return data;
	} catch (error) {
		return [];
	}
};

export const posLinks = async ({ posId, ...params }: { posId?: string } & SolicitationListQueryParams) => {
	const { status } = params;
	return requestHandler<SolicitationListResponse>(
		api.get(`/pos/management/access/${posId}`, { params: { ...params, status: status?.join(',') } }),
	);
};

export const posLinkAccessManage = (pointOfSaleCustomerIdList: string[], type: 'accept' | 'reject' | 'block') =>
	requestHandler<ResponseParams>(api.post(`/pos/management/access/${type}`, { pointOfSaleCustomerIdList }));

export const posLinkPending = async (payload: PosLinkPendingRequest) =>
	requestHandler<PosLinkPendingResponse>(api.post(`pos/management/access/pending`, payload));

export const getDataBanners = async (posId: string, tag: BannerEntity['area']['name']) => {
	return requestHandler<FullCarouselProps['data']>(api.get('/pos/banners', { params: { posId, tag } }));
};

export const getUserSimulatedPosSuggestions = async ({ cnpj }: { cnpj: string }) => {
	try {
		const response = await api.get(`/pos/list/${cnpj}`);

		return response.data.content as SimulatedPosSuggestionsResponse[];
	} catch (error) {
		if (error instanceof AxiosError) {
			throw error?.response?.data?.status;
		}

		throw error;
	}
};

export const posSupervisorChange = async () => {
	return requestHandler<SupervisorChangeResponse>(api.get('/pos/supervisor-change'));
};

export const getFlags = async (params?: GenericObject) => {
	return requestHandler<SelectedFlagResponse>(api.get('/pos/commercial-flag/list', { params }));
};

export const posLinkValidate = async (data: PosLinkValidateRequest) =>
	requestHandler<PosLinkValidateResponse>(api.post('/pos/management/access/request/validate', data), {
		throwData: true,
	});

export const posLinkGenerate = async (data: PosLinkGenerateRequest) =>
	requestHandler<PosLinkGenerateResponse>(api.post('/pos/management/access/request/generate', data), {
		throwData: true,
	});

export const posLinkStatus = async ({ accessRequestId }: { accessRequestId: ObjectID }) =>
	requestHandler<PosLinkStatusResponse>(api.get(`/pos/management/access/request/${accessRequestId}/status`), {
		throwData: true,
	});

export const searchPos = (params: Partial<SearchPosRequest>) =>
	requestHandler<SearchPosResponse[]>(api.get('/pos/search', { params }));

export function saveBankData({ posId, ...payload }: SaveBankDataRequest & { posId: string }) {
	return requestHandler(api.post(`/bank-accounts/${posId}`, payload), { throwData: true });
}

export function getBankData({ posId }: { posId: string }) {
	return requestHandler<getBankDataResponse>(api.get(`/bank-accounts/${posId}`));
}

export function checkBankExists({ posId }: { posId: string }) {
	return requestHandler<checkBankExistsResponse>(api.get(`/bank-accounts/exists/${posId}`));
}

export async function findBank(params: FindBankRequest & PaginationRequestParams) {
	return requestHandler<FindBankResponse[]>(api.get(`/banks/list`, { params }));
}
